import { styled, Box, Container, Typography, Tabs, Tab, keyframes } from "@mui/material";

// ─── Dr. Paw Blue/White Palette (mirrors home DP) ────────────
export const MEMBERSHIP_COLORS = {
  bg:           "#F0F7FF",
  bgDeep:       "#E3F2FD",
  white:        "#FFFFFF",
  blue:         "#1565C0",
  blueMid:      "#1976D2",
  blueLight:    "#42A5F5",
  bluePale:     "#BBDEFB",
  navy:         "#0A1628",
  navyMid:      "#0D2137",
  text:         "#0A1628",
  secondary:    "#37474F",
  muted:        "#78909C",
  border:       "rgba(21,101,192,0.12)",
  borderLight:  "rgba(21,101,192,0.06)",
  shadow:       "rgba(21,101,192,0.15)",
  cardBg:       "#FFFFFF",
  accent:       "#1565C0",
  navbarBg:     "#FFFFFF",
  bottomNavBg:  "#FFFFFF",
  lockedBg:     "rgba(21,101,192,0.05)",
  // keep old accent alias so existing components don't break
  primary:      "#0A1628",
};

// ─── Keyframes ────────────────────────────────────────────────
export const fadeSlideUp = keyframes`
  from { opacity:0; transform:translateY(22px); }
  to   { opacity:1; transform:translateY(0); }
`;
export const fadeIn = keyframes`
  from { opacity:0; } to { opacity:1; }
`;
export const scaleIn = keyframes`
  from { opacity:0; transform:scale(.94); }
  to   { opacity:1; transform:scale(1); }
`;
export const shimmerFlow = keyframes`
  0%   { background-position:-600px 0; }
  100% { background-position: 600px 0; }
`;
export const pulse = keyframes`
  0%,100% { box-shadow:0 0 0 0 rgba(21,101,192,.4); }
  50%      { box-shadow:0 0 0 8px rgba(21,101,192,0); }
`;

// ─── Styled Components ────────────────────────────────────────
export const MembershipWrapper = styled(Box)({
  backgroundColor: MEMBERSHIP_COLORS.bg,
  minHeight: "100vh",
  color: MEMBERSHIP_COLORS.text,
  fontFamily: "'Inter', sans-serif",
});

export const ContentContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(12),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(15),
  },
}));

export const StyledTabs = styled(Tabs)({
  borderBottom: "none",
  minHeight: "60px",
  "& .MuiTabs-indicator": {
    backgroundColor: MEMBERSHIP_COLORS.blue,
    height: 3,
    borderRadius: "2px 2px 0 0",
  },
  "& .MuiTabs-scrollButtons": {
    color: MEMBERSHIP_COLORS.muted,
    "&.Mui-disabled": { opacity: 0.3 },
  },
});

export const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
  letterSpacing: "0.02em",
  color: MEMBERSHIP_COLORS.muted,
  padding: theme.spacing(0, 3),
  minHeight: "60px",
  transition: "all 0.2s ease",
  "&.Mui-selected": { color: MEMBERSHIP_COLORS.blue, fontWeight: 700 },
  "&:hover": { color: MEMBERSHIP_COLORS.blue, opacity: 0.85 },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 2),
    fontSize: "0.8rem",
    minHeight: "52px",
  },
}));

export const VideoRowTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.15rem",
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  color: MEMBERSHIP_COLORS.text,
  transition: "color 0.2s ease",
  "&:hover": { color: MEMBERSHIP_COLORS.blue },
}));

export const HorizontalScroll = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  overflowX: "auto",
  paddingBottom: theme.spacing(2),
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
}));

export const VideoCardWrapper = styled(Box)(({ theme }) => ({
  minWidth: "280px",
  width: "280px",
  [theme.breakpoints.down("sm")]: { minWidth: "220px", width: "220px" },
}));

export const Thumbnail = styled(Box)(({ theme }) => ({
  width: "100%",
  aspectRatio: "16/9",
  backgroundColor: MEMBERSHIP_COLORS.bgDeep,
  borderRadius: "14px",
  overflow: "hidden",
  position: "relative",
  marginBottom: theme.spacing(1.5),
  transition: "transform 0.3s cubic-bezier(.34,1.56,.64,1), box-shadow 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "scale(1.03) translateY(-2px)",
    boxShadow: `0 12px 32px ${MEMBERSHIP_COLORS.shadow}`,
  },
}));

export const DurationTag = styled(Box)({
  position: "absolute",
  bottom: "8px",
  right: "8px",
  backgroundColor: "rgba(10,22,40,0.75)",
  backdropFilter: "blur(4px)",
  color: "#FFFFFF",
  padding: "2px 8px",
  borderRadius: "6px",
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
});

export const LockIconWrapper = styled(Box)({
  position: "absolute",
  top: "8px",
  left: "8px",
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});
